import React, { createContext, Suspense, lazy, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config';
import { ticketLinkTarget } from '../utils/ticketLinks';

// LAZY, and not for bundle size: importing it statically would close a cycle.
// TicketContentDisplay imports this file, ScoreDetailDrawer reaches
// TicketRichTextEditor through AppealComposer, and TicketRichTextEditor imports
// ImageLightbox back out of TicketContentDisplay. lazy() cuts the static edge,
// and the drawer is only ever needed after a click anyway.
const ScoreDetailDrawer = lazy(() => import('../pages/scores/components/ScoreDetailDrawer'));

/**
 * LinkedGradeContext — ONE drawer for every `$<id>` link in the app.
 *
 * WHY A PROVIDER AND NOT A HOOK PER SURFACE
 * -----------------------------------------
 * Feedback is rendered in ~20 places: the QA ticket dialogs, the read-only
 * Ticket Details view, /goals, /tl, calibration, coaching, active-overview,
 * the review dialog. Wiring each one by hand meant each one could be — and was
 * — forgotten; Filip found the link dead on qa-manager's view surface and on
 * /tl after four surfaces had already been done individually.
 *
 * So the click is handled once, in <TicketContentDisplay/> (the single
 * read-only feedback renderer every surface already goes through), and it
 * reaches this provider. Adding a new feedback surface now gets the link for
 * free, and there is exactly one drawer, at one z-index, in the whole app.
 *
 * SCOPE COMES FROM THE ROUTE, deliberately.
 * ScoreDetailDrawer reads a different API per scope — /api/tl/ticket on /tl,
 * /api/qa/tickets on the QA surfaces, /api/scores/.../insights on /scores —
 * because those endpoints differ in team scoping and in what they are allowed
 * to return. The route is the honest source for that: it says which set of
 * endpoints THIS page is entitled to, which is exactly the question. A caller
 * on a page whose route lies about its data source can override with
 * `openLink(id, { scope })`.
 *
 * Note this provider only opens the FIRST grade. Once the drawer is open,
 * links inside it push onto ScoreDetailDrawer's own Back stack — one stack,
 * owned by the thing that draws the Back button. Closing here returns the user
 * to whatever surface they started from, which is what "back" means at that
 * boundary.
 */

const LinkedGradeContext = createContext(null);

// A TicketContentDisplay rendered outside the provider (tests, Storybook, an
// isolated tree) must still render — it just cannot open anything.
const INERT = { openLink: () => {}, onContentClick: () => false, available: false };

export const useLinkedGrade = () => useContext(LinkedGradeContext) || INERT;

/** Which family of endpoints this page may read. See the header. */
export const drawerScopeForPath = (pathname = '') => {
  if (pathname.startsWith('/tl')) return 'tl';
  if (pathname.startsWith('/scores')) return 'scores';
  return 'qa';
};

export const LinkedGradeProvider = ({ children }) => {
  const { pathname } = useLocation();
  const [linked, setLinked] = useState(null);   // { grade, scope }
  const [loading, setLoading] = useState(false);

  // Navigating away closes it. The drawer outlives the surface it was opened
  // from — that is the point, it is mounted at the root — but it must not
  // outlive the PAGE, or a route change leaves a grade floating over an
  // unrelated screen.
  useEffect(() => { setLinked(null); }, [pathname]);

  const openLink = useCallback(async (conversationId, { agentId = null, scope = null } = {}) => {
    if (!conversationId || loading) return;
    setLoading(true);
    try {
      const res = await axios.get(
        API_URL + '/api/scores/grades/by-conversation/' + encodeURIComponent(conversationId),
        {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          params: agentId ? { agentId } : {},
        }
      );
      const g = res.data?.grade;
      if (!g?.id) { toast.error('That evaluation is no longer available'); return; }
      setLinked({
        scope: scope || drawerScopeForPath(pathname),
        grade: {
          id: g.id,
          gradableId: g.conversationId,
          scorePercent: g.score,
          dateGraded: g.gradedDate,
          market: g.market,
          isNote: g.isNote,
          // Carried so links followed INSIDE the drawer stay narrowed to the
          // same agent — the drawer passes it back as ?agentId=.
          agentId: g.agentId || agentId || null,
          categories: [],
          rubricName: null,
        },
      });
    } catch (err) {
      // 404 covers both "no such grade" and "not one you may see" on purpose:
      // telling the two apart would confirm a grade exists to somebody who may
      // not read it.
      toast.error(err.response?.status === 404
        ? 'That evaluation could not be opened'
        : 'Could not open the linked evaluation');
    } finally {
      setLoading(false);
    }
  }, [loading, pathname]);

  /**
   * Drop this on any container rendering decorated feedback. Returns true when
   * it handled the click, so a caller with its own click behaviour (an image
   * lightbox, say) can bail out early.
   */
  const onContentClick = useCallback((e) => {
    const id = ticketLinkTarget(e);
    if (!id) return false;
    openLink(id);
    return true;
  }, [openLink]);

  const value = useMemo(
    () => ({ openLink, onContentClick, loading, available: true }),
    [openLink, onContentClick, loading]
  );

  return (
    <LinkedGradeContext.Provider value={value}>
      {children}
      {/* Above every other drawer in the app (the highest local one is 10000)
          because this can be opened from inside any of them. Still below the
          image lightbox at 999999, which is transient and always on top. */}
      {linked && (
        <Suspense fallback={null}>
          <ScoreDetailDrawer
            grade={linked.grade}
            viewingOther={linked.scope !== 'scores'}
            scope={linked.scope}
            zIndex={200000}
            onClose={() => setLinked(null)}
          />
        </Suspense>
      )}
    </LinkedGradeContext.Provider>
  );
};

export default LinkedGradeContext;
